import {data, type LinksFunction, type LoaderFunctionArgs} from '@remix-run/node'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteLoaderData,
} from '@remix-run/react'
import {useEffect} from 'react'
import {Toaster, toast as showToast} from 'sonner'

import {useOptionalTheme} from '#app/routes/resources+/theme-switch'
import tailwindStylesUrl from '#app/styles/tailwind.css?url'
import {getUserId, logout} from '#app/utils/auth.server'
import {ClientHintCheck, getHints} from '#app/utils/client-hints'
import {db} from '#app/utils/db.server'
import {getEnv} from '#app/utils/env.server'
import {combineHeaders, getDomainUrl} from '#app/utils/misc'
import {getTheme, type Theme} from '#app/utils/theme.server'
import {getToast, type Toast} from '#app/utils/toast.server'

import {Footer} from './footer'
import {Header} from './header'

export const links: LinksFunction = () => [
  {rel: 'stylesheet', href: tailwindStylesUrl},
  {
    rel: 'icon',
    href: 'data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>📚</text></svg>',
  },
]

export const loader = async ({request}: LoaderFunctionArgs) => {
  const {toast, headers: toastHeaders} = await getToast(request)
  const userId = await getUserId(request)

  const user = userId
    ? await db.user.findUnique({
        where: {id: userId},
        select: {
          id: true,
          displayName: true,
          firstName: true,
          roles: {
            select: {
              name: true,
              permissions: {
                select: {
                  entity: true,
                  action: true,
                  access: true,
                },
              },
            },
          },
        },
      })
    : null

  if (userId && !user) {
    // Something went wrong, the user exists in the cookie but not in the database.
    // Maybe they were deleted? Just log them out.
    await logout({request, redirectTo: '/'})
  }

  return data(
    {
      ENV: getEnv(),
      theme: getTheme(request),
      toast,
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPreferences: {
          theme: getTheme(request),
        },
      },
    },
    {
      headers: combineHeaders(toastHeaders),
    },
  )
}

function App() {
  const data = useLoaderData<typeof loader>()

  return (
    <>
      <Header />
      <div className="my-10 flex-1">
        <Outlet />
      </div>
      <Footer />
      {data.toast ? <ShowToast toast={data.toast} /> : null}
    </>
  )
}

export function Layout({children}: {children: React.ReactNode}) {
  const data = useRouteLoaderData<typeof loader>('root')
  const theme = useOptionalTheme()

  return (
    <Document env={data?.ENV} theme={theme}>
      {children}
    </Document>
  )
}

function Document({
  children,
  theme = 'light',
  env,
}: {
  children: React.ReactNode
  theme?: Theme
  env?: Record<string, unknown>
}) {
  return (
    <html className={`${theme}`} lang="en">
      <head>
        <ClientHintCheck />
        <Meta />
        <meta charSet="utf-8" />
        <meta content="width=device-width,initial-scale=1" name="viewport" />
        <Links />
      </head>
      <body className="min-h-screen bg-background text-foreground">
        <div className="relative flex min-h-screen flex-col bg-background">{children}</div>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <Toaster
          closeButton
          position="top-center"
          richColors
          offset={72}
          toastOptions={{
            classNames: {
              toast: '!items-start !gap-1',
            },
          }}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

function ShowToast({toast}: {toast: Toast}) {
  const {id, type, title, description} = toast
  useEffect(() => {
    setTimeout(() => {
      showToast[type](title, {id, description})
    }, 0)
  }, [description, id, title, type])
  return null
}

function AppWithProviders() {
  return <App />
}

export default AppWithProviders

export {ErrorBoundary} from './components/error-boundary'
